import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Create from "@mui/icons-material/Create";
import OpenWith from "@mui/icons-material/OpenWith";
import LineStyle from "@mui/icons-material/LineStyle";
import AddLocationAlt from "@mui/icons-material/AddLocationAlt";
import Polyline from "@mui/icons-material/Polyline";
import HexagonOutlined from "@mui/icons-material/HexagonOutlined";
import CropSquare from "@mui/icons-material/CropSquare";
import RotateRight from "@mui/icons-material/RotateRight";
import PhotoSizeSelectLarge from "@mui/icons-material/PhotoSizeSelectLarge";
import TouchApp from "@mui/icons-material/TouchApp";
import LayersOutlined from "@mui/icons-material/LayersOutlined";
import Refresh from "@mui/icons-material/Refresh";
import Feature from "ol/Feature";
import Collection from "ol/Collection";
import "ol/ol.css";
import "./overrideMapEditor.css";
import { Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import type TileWMS from "ol/source/TileWMS";
import VectorLayer from "ol/layer/Vector";
import VectorTileLayer from "ol/layer/VectorTile";
import OSM from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import Draw, { createRegularPolygon } from "ol/interaction/Draw";
import { Modify, Snap, Translate } from "ol/interaction";
import Select, { SelectEvent } from "ol/interaction/Select";
import VectorTileAwareSelect from "./vectorTileSelectInteraction";
import DragBox from "ol/interaction/DragBox";
import {
  always,
  click as mapBrowserClick,
  platformModifierKeyOnly,
  singleClick as mapBrowserSingleClick,
} from "ol/events/condition";
import { Fill, Stroke, Style } from "ol/style";
import { fromLonLat } from "ol/proj";
import { getUid } from "ol/util";
import { fromCircle } from "ol/geom/Polygon";
import type CircleGeom from "ol/geom/Circle";
import {
  createRiskOverride,
  deleteRiskOverride,
  getRiskOverride,
  patchRiskOverride,
  formatReturnPeriod,
  parseReturnPeriodInput,
  type RiskOverrideDTO,
  type RiskOverrideKind,
  type RiskOverrideScope,
  type RiskOverrideStatus,
} from "./riskOverrideApi";
import { getOverrideDatasetGeoLayers } from "../map/core/riskLayerCatalog";
import {
  fetchGeoServerFeatureJsonById,
  resolveMvtFeatureIdForWfs,
  resolveOverrideDatasetWfsTypeName,
} from "../map/core/geoServerWfs";
import {
  createGeoServerMvtFromWmsLayer,
  createGeoServerRiskTileLayer,
  createMvtRiskLevelStyleFromScaleLevels,
  RISK_MAP_LAYER_OPACITY,
  readRiskLevelFromMvtFeature,
} from "../map/core/geoServerRiskLayer";
import EdgeMoveInteraction from "./edgeMoveInteraction";
import { RotateShapeInteraction, ScaleShapeInteraction } from "./shapeRotateScaleInteractions";
import { scaleLevels } from "../../constants/search";

/** Propriété OL : niveau de risque au moment du tracé (aligné sur le formulaire). */
const OVERRIDE_FEATURE_RISK_LEVEL = "overrideRiskLevel";

/** Référentiel coastal_erosion.id — upsert si défini. */
const OVERRIDE_SOURCE_FEATURE_ID = "overrideSourceFeatureId";

/** Id ligne override PostGIS — PATCH au lieu de POST. */
const OVERRIDE_EXISTING_OVERRIDE_ID = "overrideExistingOverrideId";

const OVERRIDE_KIND = "overrideKind";
const OVERRIDE_SCOPE = "overrideScope";
const OVERRIDE_TENANT_ID = "overrideTenantId";
const OVERRIDE_STATUS = "overrideStatus";
const OVERRIDE_PRIORITY = "overridePriority";
const OVERRIDE_NOTE = "overrideNote";
const OVERRIDE_RETURN_PERIOD = "overrideReturnPeriod";

type DrawShapeParams = {
  overrideKind: RiskOverrideKind;
  scope: RiskOverrideScope;
  tenantId: string;
  riskLevel: number;
  status: RiskOverrideStatus;
  priority: number;
  note: string;
  /** Saisie libre : "" | "10" | "10-30" | "1000+". */
  returnPeriod: string;
};

function parseRiskOverrideKind(v: unknown): RiskOverrideKind {
  const s = String(v ?? "").trim();
  if (s === "add" || s === "exclude" || s === "replace") {
    return s;
  }
  return "replace";
}

function parseScope(v: unknown): RiskOverrideScope {
  return String(v ?? "").trim() === "tenant" ? "tenant" : "global";
}

function parseStatus(v: unknown): RiskOverrideStatus {
  return String(v ?? "").trim() === "published" ? "published" : "draft";
}

function mvtFeatureProp(feature: Feature, keys: string[]): unknown {
  for (const k of keys) {
    const v = feature.get(k);
    if (v !== undefined && v !== null && v !== "") {
      return v;
    }
  }
  return undefined;
}

function paramsFromRiskOverrideDto(dto: RiskOverrideDTO): DrawShapeParams {
  return {
    overrideKind: dto.overrideKind ?? "replace",
    scope: dto.scope ?? "global",
    tenantId: dto.tenantId ?? "",
    riskLevel:
      dto.riskLevel != null && Number.isFinite(dto.riskLevel)
        ? Math.trunc(dto.riskLevel)
        : 3,
    status: dto.status ?? "draft",
    priority: typeof dto.priority === "number" ? dto.priority : 0,
    note: dto.note ?? "",
    returnPeriod: formatReturnPeriod(dto.returnPeriodMin, dto.returnPeriodMax),
  };
}

function readMvtOverrideParams(feature: Feature): DrawShapeParams {
  const rlRaw = mvtFeatureProp(feature, ["risk_level", "riskLevel", "rl"]);
  let riskLevel = 3;
  if (rlRaw !== undefined) {
    const n = typeof rlRaw === "number" ? rlRaw : Number(String(rlRaw));
    if (Number.isFinite(n) && n >= 0 && n <= 5) {
      riskLevel = Math.trunc(n);
    }
  } else {
    const fromTaux = readRiskLevelFromMvtFeature(feature);
    if (fromTaux >= 0 && fromTaux <= 5) {
      riskLevel = fromTaux;
    }
  }
  const noteRaw = mvtFeatureProp(feature, ["note"]);
  const rpMinRaw = mvtFeatureProp(feature, [
    "return_period_min",
    "returnPeriodMin",
  ]);
  const rpMaxRaw = mvtFeatureProp(feature, [
    "return_period_max",
    "returnPeriodMax",
  ]);
  const rpMin =
    rpMinRaw != null && Number.isFinite(Number(rpMinRaw))
      ? Number(rpMinRaw)
      : null;
  const rpMax =
    rpMaxRaw != null && Number.isFinite(Number(rpMaxRaw))
      ? Number(rpMaxRaw)
      : null;
  return {
    overrideKind: parseRiskOverrideKind(
      mvtFeatureProp(feature, ["override_kind", "overrideKind"]),
    ),
    scope: parseScope(mvtFeatureProp(feature, ["scope"])),
    tenantId: String(mvtFeatureProp(feature, ["tenant_id", "tenantId"]) ?? "").trim(),
    riskLevel,
    status: parseStatus(mvtFeatureProp(feature, ["status"])),
    priority: Number(mvtFeatureProp(feature, ["priority"]) ?? 0) || 0,
    note: noteRaw != null ? String(noteRaw) : "",
    returnPeriod: formatReturnPeriod(rpMin, rpMax),
  };
}

function readDrawFeatureParams(feature: Feature): DrawShapeParams {
  const rl = feature.get(OVERRIDE_FEATURE_RISK_LEVEL);
  return {
    overrideKind: parseRiskOverrideKind(feature.get(OVERRIDE_KIND)),
    scope: parseScope(feature.get(OVERRIDE_SCOPE)),
    tenantId: String(feature.get(OVERRIDE_TENANT_ID) ?? "").trim(),
    riskLevel: typeof rl === "number" && Number.isFinite(rl) ? Math.trunc(rl) : 3,
    status: parseStatus(feature.get(OVERRIDE_STATUS)),
    priority: Number(feature.get(OVERRIDE_PRIORITY) ?? 0) || 0,
    note: String(feature.get(OVERRIDE_NOTE) ?? ""),
    returnPeriod: String(feature.get(OVERRIDE_RETURN_PERIOD) ?? ""),
  };
}

function writeDrawFeatureParams(feature: Feature, params: DrawShapeParams): void {
  feature.set(OVERRIDE_KIND, params.overrideKind);
  feature.set(OVERRIDE_SCOPE, params.scope);
  feature.set(OVERRIDE_TENANT_ID, params.tenantId);
  feature.set(OVERRIDE_FEATURE_RISK_LEVEL, params.riskLevel);
  feature.set(OVERRIDE_STATUS, params.status);
  feature.set(OVERRIDE_PRIORITY, params.priority);
  feature.set(OVERRIDE_NOTE, params.note);
  feature.set(OVERRIDE_RETURN_PERIOD, params.returnPeriod);
}

/** VIEWPARAMS GeoServer (fn_coastal_erosion_override, fn_coastal_erosion_final). */
function buildMapTenantViewParams(
  mapScope: RiskOverrideScope,
  mapTenantId: string,
): Record<string, string> | undefined {
  if (mapScope === "tenant" && mapTenantId.trim() !== "") {
    return { tenant_id: mapTenantId.trim() };
  }
  return undefined;
}

function isMvtFeatureFromPersistedOverrides(
  feat: Feature,
  datasetKey: string,
): boolean {
  const def = getOverrideDatasetGeoLayers(datasetKey);
  const tn = resolveOverrideDatasetWfsTypeName(datasetKey, feat);
  if (!def?.overrides || !tn) {
    return false;
  }
  return tn === def.overrides;
}

function riskLevelHex(level: number): string {
  const entry = scaleLevels[level];
  if (entry?.color) {
    return entry.color;
  }
  return scaleLevels[-1]?.color ?? "#888888";
}

function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.trim().replace(/^#/, "");
  if (normalized.length !== 6) {
    return `rgba(128, 128, 128, ${alpha})`;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Remplissage : niveaux 3–5 très proches en teinte dans scaleLevels → on étale l’opacité pour les distinguer sur la carte. */
function overrideFillAlpha(level: number, selected: boolean, active: boolean): number {
  const base =
    level <= 2 ? 0.28 : level === 3 ? 0.22 : level === 4 ? 0.34 : 0.4;
  if (active) {
    return Math.min(base + 0.16, 0.55);
  }
  return selected ? Math.min(base + 0.1, 0.48) : base;
}

/** Contour : épaisseur progressive au-delà du niveau élevé pour aider à lire 3 vs 4 vs 5. */
function overrideStrokeWidth(level: number, selected: boolean, active: boolean): number {
  const base =
    level <= 2 ? 2 : level === 3 ? 2 : level === 4 ? 2.85 : 3.6;
  if (active) {
    return base + 2.2;
  }
  return selected ? base + 0.85 : base;
}

function styleOverrideFeature(
  feature: import("ol/Feature").FeatureLike,
  selected: boolean,
  active: boolean,
  fallbackLevel: number,
): Style | Style[] {
  const f = feature as Feature;
  const stored = f.get(OVERRIDE_FEATURE_RISK_LEVEL);
  const level = typeof stored === "number" ? stored : fallbackLevel;
  const hex = riskLevelHex(level);
  const fill = new Fill({ color: hexToRgba(hex, overrideFillAlpha(level, selected, active)) });
  const strokeW = overrideStrokeWidth(level, selected, active);
  const stroke = new Stroke({ color: hex, width: strokeW });
  if (active) {
    return [
      new Style({
        fill,
        stroke: new Stroke({ color: "rgba(255, 255, 255, 0.95)", width: strokeW + 3 }),
      }),
      new Style({
        fill,
        stroke: new Stroke({ color: "#1565c0", width: strokeW + 1.25 }),
      }),
      new Style({ fill, stroke }),
    ];
  }
  return new Style({ fill, stroke });
}

type DatasetOption = {
  key: string;
  label: string;
};

const DATASET_OPTIONS: DatasetOption[] = [
  { key: "coastal_erosion", label: "Coastal erosion" },
  { key: "submersion", label: "Submersion marine" },
  { key: "flood", label: "Inondation" },
];

/** Polygone libre, octogone régulier (8 côtés), carré régulier (4 côtés). */
export type DrawShapeMode = "polygon" | "octagon" | "square";

/** Outil actif sur la carte : dessin, transformations, déplacement d’un côté, ajout de sommets. */
export type MapEditTool =
  | "draw"
  | "moveShape"
  | "rotateShape"
  | "scaleShape"
  | "moveEdge"
  | "addVertex"
  | "select"
  /** Polygones des tuiles MVT (référentiel + overrides persistés GeoServer). */
  | "selectMvt";

/** Conversion des rares géométries OL Circle (legacy) en polygone pour l’API — même granularité que l’octogone. */
const CIRCLE_TO_POLYGON_SIDES = 8;

/** Style MVT partagé (référentiel, overrides persistés) : couleurs `scaleLevels`. */
const MVT_RISK_LEVEL_STYLE = createMvtRiskLevelStyleFromScaleLevels();

const RISK_LEVEL_MIN = 0;
const RISK_LEVEL_MAX = 5;

/** Mise en évidence des polygones sélectionnés dans les couches MVT (tuiles vectorielles). */
const MVT_TILE_SELECTED_STYLE = new Style({
  fill: new Fill({ color: "rgba(255, 235, 59, 0.38)" }),
  stroke: new Stroke({ color: "#f57f17", width: 2.75 }),
});

function formatMvtInspectorValue(v: unknown): string {
  if (v == null) {
    return "—";
  }
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/** Attributs MVT visibles dans le panneau (hors géométrie et métadonnées internes `_…`). */
function mvtInspectorPropertyRows(feature: Feature): { key: string; value: string }[] {
  const geomKey = feature.getGeometryName();
  const props = feature.getProperties();
  return Object.keys(props)
    .filter((k) => k !== geomKey && !k.startsWith("_"))
    .sort((a, b) => a.localeCompare(b))
    .map((k) => ({ key: k, value: formatMvtInspectorValue(props[k]) }));
}

export default function OverrideMapEditor() {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const baseRiskWmsLayerRef = useRef<TileLayer<TileWMS> | null>(null);
  const baseRiskLayerRef = useRef<VectorTileLayer | null>(null);
  const storedOverrideLayerRef = useRef<VectorTileLayer | null>(null);
  /** Carte finale en mode aperçu : WMS PNG (style GeoServer), pas MVT. */
  const finalComposedLayerRef = useRef<TileLayer<TileWMS> | null>(null);
  const refreshPublishedTilesRef = useRef<(() => void) | null>(null);
  const refreshFinalTilesOnlyRef = useRef<(() => void) | null>(null);
  const mergePreviewOnlyRef = useRef(false);
  const showBaseMvtLayerRef = useRef(true);
  const showStoredMvtLayerRef = useRef(true);
  const drawSourceRef = useRef<VectorSource | null>(null);
  const drawLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const drawInteractionRef = useRef<Draw | null>(null);
  const modifyInteractionRef = useRef<Modify | null>(null);
  const translateInteractionRef = useRef<Translate | null>(null);
  const edgeMoveInteractionRef = useRef<EdgeMoveInteraction | null>(null);
  const rotateShapeInteractionRef = useRef<RotateShapeInteraction | null>(null);
  const scaleShapeInteractionRef = useRef<ScaleShapeInteraction | null>(null);
  const snapInteractionRef = useRef<Snap | null>(null);
  const selectedFeaturesRef = useRef<Collection<Feature> | null>(null);
  /** Forme d’édition « active » (une seule) ; les autres peuvent rester sélectionnées. */
  const activeDrawFeatureRef = useRef<Feature | null>(null);
  const selectInteractionRef = useRef<Select | null>(null);
  const dragBoxInteractionRef = useRef<DragBox | null>(null);
  const mvtTileSelectedFeaturesRef = useRef<Collection<Feature> | null>(null);
  const mvtTileSelectInteractionRef = useRef<Select | null>(null);
  /** Reprise visuelle des clones MVT : ils ne sont pas dans une VectorTileSource, le style du Select seul ne dessine rien. */
  const mvtHighlightSourceRef = useRef<VectorSource | null>(null);
  const mvtHighlightLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

  const [dataset, setDataset] = useState<string>(DATASET_OPTIONS[0].key);
  const [drawShapeMode, setDrawShapeMode] = useState<DrawShapeMode>("polygon");
  const [mapTool, setMapTool] = useState<MapEditTool>("draw");
  /** Contexte carte : filtre MVT/WFS (VIEWPARAM tenant_id). */
  const [mapScope, setMapScope] = useState<RiskOverrideScope>("global");
  const [mapTenantId, setMapTenantId] = useState<string>("");
  /** Propriétés de la forme active (ou défauts pour nouveau tracé). */
  const [scope, setScope] = useState<RiskOverrideScope>("global");
  const [tenantId, setTenantId] = useState<string>("");
  const [riskLevel, setRiskLevel] = useState<number>(3);
  const [status, setStatus] = useState<RiskOverrideStatus>("draft");
  const [priority, setPriority] = useState<number>(0);
  const [note, setNote] = useState<string>("");
  const [returnPeriod, setReturnPeriod] = useState<string>("");
  const [overrideKind, setOverrideKind] = useState<RiskOverrideKind>("replace");
  const [upsertBySource, setUpsertBySource] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [featureCount, setFeatureCount] = useState<number>(0);
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [activeDrawFeatureUid, setActiveDrawFeatureUid] = useState<string | null>(null);
  const [mvtTileSelectedCount, setMvtTileSelectedCount] = useState<number>(0);
  /** Incrémenté à chaque changement de la collection MVT pour réafficher l’inspecteur. */
  const [mvtSelectionTick, setMvtSelectionTick] = useState(0);
  const [wfsLoadingUid, setWfsLoadingUid] = useState<string | null>(null);
  const [wfsLastError, setWfsLastError] = useState<string | null>(null);
  const [mvtDeletingUid, setMvtDeletingUid] = useState<string | null>(null);
  /** Aperçu seul : uniquement la couche WMS PNG « carte finale » (style GeoServer), référentiel + overrides masqués. */
  const [mergePreviewOnly, setMergePreviewOnly] = useState(false);
  /** Hors aperçu : afficher ou masquer les MVT référentiel / overrides (indépendamment). */
  const [showBaseMvtLayer, setShowBaseMvtLayer] = useState(true);
  const [showStoredMvtLayer, setShowStoredMvtLayer] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  /** Debug : zoom courant de la View OL (change:resolution). */
  const [mapZoomDisplay, setMapZoomDisplay] = useState<number | null>(null);

  mergePreviewOnlyRef.current = mergePreviewOnly;
  showBaseMvtLayerRef.current = showBaseMvtLayer;
  showStoredMvtLayerRef.current = showStoredMvtLayer;

  const riskLevelRef = useRef(riskLevel);
  riskLevelRef.current = riskLevel;

  const formParamsRef = useRef<DrawShapeParams>({
    overrideKind: "replace",
    scope: "global",
    tenantId: "",
    riskLevel: 3,
    status: "draft",
    priority: 0,
    note: "",
    returnPeriod: "",
  });
  formParamsRef.current = {
    overrideKind,
    scope,
    tenantId,
    riskLevel,
    status,
    priority,
    note,
    returnPeriod,
  };

  const formSyncRef = useRef<(p: DrawShapeParams) => void>(() => {});
  formSyncRef.current = (p) => {
    setOverrideKind(p.overrideKind);
    setScope(p.scope);
    setTenantId(p.tenantId);
    setRiskLevel(p.riskLevel);
    setStatus(p.status);
    setPriority(p.priority);
    setNote(p.note);
    setReturnPeriod(p.returnPeriod);
  };

  const setActiveDrawFeature = useCallback((feat: Feature | null) => {
    activeDrawFeatureRef.current = feat;
    setActiveDrawFeatureUid(feat ? String(getUid(feat)) : null);
    if (feat) {
      formSyncRef.current(readDrawFeatureParams(feat));
    }
    drawLayerRef.current?.changed();
  }, []);

  const patchActiveShapeForm = useCallback((patch: Partial<DrawShapeParams>) => {
    const active = activeDrawFeatureRef.current;
    const base = active ? readDrawFeatureParams(active) : formParamsRef.current;
    const next = { ...base, ...patch };
    formSyncRef.current(next);
    if (active) {
      writeDrawFeatureParams(active, next);
      active.changed();
      drawLayerRef.current?.changed();
    }
  }, []);

  const clearMvtTileSelectionRef = useRef<() => void>(() => {});
  const clearMvtTileSelection = useCallback(() => {
    const col = mvtTileSelectedFeaturesRef.current;
    if (col && col.getLength() > 0) {
      col.clear();
    }
  }, []);
  clearMvtTileSelectionRef.current = clearMvtTileSelection;

  void activeDrawFeatureUid;

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) {
      return;
    }

    const drawSource = new VectorSource();
    drawSourceRef.current = drawSource;
    const selectedFeatures = new Collection<Feature>();
    selectedFeaturesRef.current = selectedFeatures;

    drawSource.on("addfeature", (e) => {
      const added = e.feature;
      if (added) {
        const f = added as Feature;
        // Copie MVT / import : propriétés déjà posées — ne pas les écraser par le formulaire courant.
        const copiedFromMvt = f.get(OVERRIDE_KIND) != null;
        if (!copiedFromMvt) {
          writeDrawFeatureParams(f, formParamsRef.current);
        }
        selectedFeatures.clear();
        selectedFeatures.push(f);
        activeDrawFeatureRef.current = f;
        setActiveDrawFeatureUid(String(getUid(f)));
        formSyncRef.current(readDrawFeatureParams(f));
      }
      setFeatureCount(drawSource.getFeatures().length);
      drawLayerRef.current?.changed();
    });

    const drawLayer = new VectorLayer({
      source: drawSource,
      style: (feature) => {
        const f = feature as Feature;
        const selected = selectedFeaturesRef.current?.getArray() ?? [];
        const active = activeDrawFeatureRef.current;
        return styleOverrideFeature(
          f,
          selected.includes(f),
          active === f,
          riskLevelRef.current,
        );
      },
      zIndex: 20,
    });
    drawLayerRef.current = drawLayer;

    selectedFeatures.on(["add", "remove"], () => {
      const active = activeDrawFeatureRef.current;
      const arr = selectedFeatures.getArray();
      if (active && !arr.includes(active)) {
        activeDrawFeatureRef.current = arr.length > 0 ? arr[arr.length - 1]! : null;
        setActiveDrawFeatureUid(
          activeDrawFeatureRef.current
            ? String(getUid(activeDrawFeatureRef.current))
            : null,
        );
        if (activeDrawFeatureRef.current) {
          formSyncRef.current(readDrawFeatureParams(activeDrawFeatureRef.current));
        }
      }
      drawLayer.changed();
      setSelectedCount(selectedFeatures.getLength());
      if (arr.length === 0) {
        clearMvtTileSelectionRef.current();
      }
    });

    const mvtTileSelectedFeatures = new Collection<Feature>();
    mvtTileSelectedFeaturesRef.current = mvtTileSelectedFeatures;
    mvtTileSelectedFeatures.on(["add", "remove"], () => {
      setMvtTileSelectedCount(mvtTileSelectedFeatures.getLength());
      setMvtSelectionTick((n) => n + 1);
    });

    const mvtHighlightSource = new VectorSource();
    mvtHighlightSourceRef.current = mvtHighlightSource;
    const mvtHighlightLayer = new VectorLayer({
      source: mvtHighlightSource,
      style: MVT_TILE_SELECTED_STYLE,
      zIndex: 15,
      properties: { role: "mvt-selection-highlight" },
    });
    mvtHighlightLayerRef.current = mvtHighlightLayer;

    const syncMvtHighlightLayer = () => {
      mvtHighlightSource.clear();
      const list = mvtTileSelectedFeatures.getArray();
      if (list.length > 0) {
        mvtHighlightSource.addFeatures(list);
      }
    };
    mvtTileSelectedFeatures.on(["add", "remove"], syncMvtHighlightLayer);

    const map = new Map({
      target: mapElementRef.current,
      layers: [
        new TileLayer({ source: new OSM() }),
        drawLayer,
      ],
      view: new View({
        center: fromLonLat([2.333333, 48.866667]),
        zoom: 8,
      }),
      pixelRatio: 1,
      /** Limite les GetMap WMS concurrents (aperçu final fn_*_final). Les MVT ne passent pas par cette file. */
      maxTilesLoading: 4,
    });
    mapRef.current = map;
    map.addLayer(mvtHighlightLayer);
    (window as any).__overrideMap = map;

    const syncMapZoomDisplay = () => {
      const z = map.getView().getZoom();
      setMapZoomDisplay(typeof z === "number" && Number.isFinite(z) ? z : null);
    };
    syncMapZoomDisplay();
    map.getView().on("change:resolution", syncMapZoomDisplay);

    const snap = new Snap({ source: drawSource });
    map.addInteraction(snap);
    snapInteractionRef.current = snap;

    const mapViewport = map.getTargetElement();
    /** Ctrl / ⌘ + molette : évite Maj (sélection multiple). preventDefault dès qu’il y a un delta pour que le zoom page ne capte pas un des deux sens. */
    const onWheelModifierRiskLevel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        return;
      }
      if (selectedFeatures.getLength() === 0) {
        return;
      }
      let dy = e.deltaY;
      if (dy === 0 && e.deltaX !== 0) {
        dy = e.deltaX;
      }
      if (dy === 0) {
        return;
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      /* deltaY < 0 : molette vers le haut → +1 niveau ; > 0 → −1 */
      const step = dy < 0 ? 1 : -1;
      for (const feat of selectedFeatures.getArray()) {
        const cur =
          typeof feat.get(OVERRIDE_FEATURE_RISK_LEVEL) === "number"
            ? (feat.get(OVERRIDE_FEATURE_RISK_LEVEL) as number)
            : riskLevelRef.current;
        const next = Math.min(RISK_LEVEL_MAX, Math.max(RISK_LEVEL_MIN, cur + step));
        feat.set(OVERRIDE_FEATURE_RISK_LEVEL, next);
        feat.changed();
      }
      const active = activeDrawFeatureRef.current;
      if (active) {
        const rl = active.get(OVERRIDE_FEATURE_RISK_LEVEL);
        if (typeof rl === "number") {
          setRiskLevel(rl);
        }
      }
      drawLayer.changed();
    };
    if (mapViewport) {
      mapViewport.addEventListener("wheel", onWheelModifierRiskLevel, {
        capture: true,
        passive: false,
      });
    }

    return () => {
      map.getView().un("change:resolution", syncMapZoomDisplay);
      if (mapViewport) {
        mapViewport.removeEventListener("wheel", onWheelModifierRiskLevel, {
          capture: true,
        });
      }
      setMapZoomDisplay(null);
      drawInteractionRef.current = null;
      modifyInteractionRef.current = null;
      translateInteractionRef.current = null;
      edgeMoveInteractionRef.current = null;
      rotateShapeInteractionRef.current = null;
      scaleShapeInteractionRef.current = null;
      drawLayerRef.current = null;
      selectedFeaturesRef.current = null;
      selectInteractionRef.current = null;
      dragBoxInteractionRef.current = null;
      mvtTileSelectInteractionRef.current = null;
      mvtTileSelectedFeaturesRef.current = null;
      mvtHighlightSourceRef.current = null;
      mvtHighlightLayerRef.current = null;
      activeDrawFeatureRef.current = null;
      snapInteractionRef.current = null;
      baseRiskLayerRef.current = null;
      storedOverrideLayerRef.current = null;
      finalComposedLayerRef.current = null;
      refreshPublishedTilesRef.current = null;
      refreshFinalTilesOnlyRef.current = null;
      map.setTarget(undefined);
      mapRef.current = null;
      drawSourceRef.current = null;
      setFeatureCount(0);
      setSelectedCount(0);
      setMvtTileSelectedCount(0);
      setMvtSelectionTick(0);
    };
  }, []);

  /**
   * Un seul outil “édition” actif : dessin, translation de la forme entière, modification par côté
   * (segment sans insertion au clic), ou ajout de sommet au clic sur un côté (comportement Modify classique).
   */
  useEffect(() => {
    const map = mapRef.current;
    const drawSource = drawSourceRef.current;
    const drawLayer = drawLayerRef.current;
    if (!map || !drawSource || !drawLayer) {
      return;
    }

    if (drawInteractionRef.current) {
      map.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }
    if (modifyInteractionRef.current) {
      map.removeInteraction(modifyInteractionRef.current);
      modifyInteractionRef.current = null;
    }
    if (translateInteractionRef.current) {
      map.removeInteraction(translateInteractionRef.current);
      translateInteractionRef.current = null;
    }
    if (edgeMoveInteractionRef.current) {
      map.removeInteraction(edgeMoveInteractionRef.current);
      edgeMoveInteractionRef.current = null;
    }
    if (rotateShapeInteractionRef.current) {
      map.removeInteraction(rotateShapeInteractionRef.current);
      rotateShapeInteractionRef.current = null;
    }
    if (scaleShapeInteractionRef.current) {
      map.removeInteraction(scaleShapeInteractionRef.current);
      scaleShapeInteractionRef.current = null;
    }
    if (selectInteractionRef.current) {
      map.removeInteraction(selectInteractionRef.current);
      selectInteractionRef.current = null;
    }
    if (dragBoxInteractionRef.current) {
      map.removeInteraction(dragBoxInteractionRef.current);
      dragBoxInteractionRef.current = null;
    }
    if (mvtTileSelectInteractionRef.current) {
      map.removeInteraction(mvtTileSelectInteractionRef.current);
      mvtTileSelectInteractionRef.current = null;
    }

    if (mapTool !== "selectMvt") {
      clearMvtTileSelection();
    }

    /**
     * OpenLayers exécute les interactions du dernier indice vers 0. Il faut donc que l’outil éditable
     * soit avant le DragPan (sinon la carte pan au clic) et, pour le snap, que Snap reste le dernier
     * de la collection : ordre d’exécution = Snap → outil → … → pan.
     * On insère l’outil à l’index de Snap (Snap repoussé d’un cran, toujours en dernière position).
     */
    const addToolInteraction = (
      interaction:
        | Draw
        | Modify
        | Translate
        | EdgeMoveInteraction
        | RotateShapeInteraction
        | ScaleShapeInteraction
        | Select
        | DragBox,
    ) => {
      const coll = map.getInteractions();
      const snap = snapInteractionRef.current;
      if (snap) {
        const idx = coll.getArray().indexOf(snap);
        if (idx >= 0) {
          coll.insertAt(idx, interaction);
          return;
        }
      }
      coll.push(interaction);
    };

    /** Nettoyé dans le return du même effet ; évite que le clic carte traite l’événement avant l’annulation. */
    let unregisterDrawAbortListeners: (() => void) | undefined;

    switch (mapTool) {
      case "moveShape": {
        const tr = new Translate({
          layers: (layer) => layer === drawLayer,
          hitTolerance: 6,
        });
        addToolInteraction(tr);
        translateInteractionRef.current = tr;
        break;
      }
      case "rotateShape": {
        const rot = new RotateShapeInteraction({
          layer: drawLayer,
          circleToPolygonSides: CIRCLE_TO_POLYGON_SIDES,
        });
        addToolInteraction(rot);
        rotateShapeInteractionRef.current = rot;
        break;
      }
      case "scaleShape": {
        const sc = new ScaleShapeInteraction({
          layer: drawLayer,
          circleToPolygonSides: CIRCLE_TO_POLYGON_SIDES,
        });
        addToolInteraction(sc);
        scaleShapeInteractionRef.current = sc;
        break;
      }
      case "moveEdge": {
        const edgeMove = new EdgeMoveInteraction({
          source: drawSource,
          layer: drawLayer,
          pixelTolerance: 12,
          vertexExclusionPx: 10,
        });
        addToolInteraction(edgeMove);
        edgeMoveInteractionRef.current = edgeMove;
        break;
      }
      case "addVertex": {
        const mod = new Modify({
          source: drawSource,
          insertVertexCondition: always,
        });
        addToolInteraction(mod);
        modifyInteractionRef.current = mod;
        break;
      }
      case "select": {
        const selected = selectedFeaturesRef.current;
        if (!selected) {
          break;
        }
        const sel = new Select({
          layers: (l) => l === drawLayer,
          features: selected,
          style: null,
          hitTolerance: 8,
        });
        sel.on("select", (evt: SelectEvent) => {
          let next: Feature | null = null;
          if (evt.selected.length > 0) {
            next = evt.selected[evt.selected.length - 1] as Feature;
          } else if (selected.getLength() > 0) {
            next = selected.item(selected.getLength() - 1);
          }
          setActiveDrawFeature(next);
          clearMvtTileSelection();
        });
        addToolInteraction(sel);
        selectInteractionRef.current = sel;

        const dragBox = new DragBox({
          condition: platformModifierKeyOnly,
          className: "risk-override-box-select",
        });
        dragBox.on("boxend", (evt) => {
          const geom = dragBox.getGeometry();
          const extent = geom.getExtent();
          const sourceNow = drawSourceRef.current;
          const collection = selectedFeaturesRef.current;
          if (!sourceNow || !collection) {
            return;
          }
          const found = sourceNow.getFeaturesInExtent(extent) as Feature[];
          const orig = evt.mapBrowserEvent.originalEvent;
          const additive = "shiftKey" in orig && (orig as MouseEvent).shiftKey;
          if (!additive) {
            collection.clear();
          }
          const already = new Set(collection.getArray());
          found.forEach((f) => {
            if (!already.has(f)) {
              collection.push(f);
              already.add(f);
            }
          });
          if (found.length > 0) {
            setActiveDrawFeature(found[found.length - 1]!);
          }
          clearMvtTileSelection();
        });
        addToolInteraction(dragBox);
        dragBoxInteractionRef.current = dragBox;
        break;
      }
      case "selectMvt": {
        const col = mvtTileSelectedFeaturesRef.current;
        if (!col) {
          break;
        }
        const mvtSel = new VectorTileAwareSelect({
          layers: (layer) =>
            layer === baseRiskLayerRef.current ||
            layer === storedOverrideLayerRef.current,
          features: col,
          /**
           * Conditions OL portent sur `mapBrowserEvent.type`, pas sur `originalEvent.type`.
           * Accepter `click` (après pointerup) et `singleclick` (différé ~250 ms) pour la sélection.
           */
          condition: (e) =>
            mapBrowserClick(e) || mapBrowserSingleClick(e),
          hitTolerance: 10,
          /** Surbrillance via `mvtHighlightLayer` : les clones ne sont pas rendus par la VectorTile. */
          style: null,
        });
        addToolInteraction(mvtSel);
        mvtTileSelectInteractionRef.current = mvtSel;
        break;
      }
      case "draw":
      default: {
        let draw: Draw;
        switch (drawShapeMode) {
          case "octagon":
            draw = new Draw({
              source: drawSource,
              type: "Circle",
              geometryFunction: createRegularPolygon(8, 0),
            });
            break;
          case "square":
            /* π/4 : côtés parallèles aux axes carte ; avec angle 0 les sommets tombent sur E/N/O/S. */
            draw = new Draw({
              source: drawSource,
              type: "Circle",
              geometryFunction: createRegularPolygon(4, Math.PI / 4),
            });
            break;
          case "polygon":
          default:
            draw = new Draw({ source: drawSource, type: "Polygon" });
            break;
        }
        addToolInteraction(draw);
        drawInteractionRef.current = draw;

        const targetEl = map.getTargetElement();
        if (targetEl) {
          const onKeyDownCapture = (e: KeyboardEvent) => {
            if (e.key !== "Escape") {
              return;
            }
            if (
              (e.target as HTMLElement | null)?.closest(
                "input, textarea, select, [contenteditable=true]",
              )
            ) {
              return;
            }
            drawInteractionRef.current?.abortDrawing();
            e.preventDefault();
            e.stopPropagation();
          };

          const onPointerDownCapture = (e: PointerEvent) => {
            if (e.button !== 2) {
              return;
            }
            if (!targetEl.contains(e.target as Node)) {
              return;
            }
            drawInteractionRef.current?.abortDrawing();
            e.preventDefault();
            e.stopPropagation();
          };

          const onContextMenuCapture = (e: MouseEvent) => {
            if (!targetEl.contains(e.target as Node)) {
              return;
            }
            drawInteractionRef.current?.abortDrawing();
            e.preventDefault();
            e.stopPropagation();
          };

          window.addEventListener("keydown", onKeyDownCapture, true);
          targetEl.addEventListener("pointerdown", onPointerDownCapture, true);
          targetEl.addEventListener("contextmenu", onContextMenuCapture, true);

          unregisterDrawAbortListeners = () => {
            window.removeEventListener("keydown", onKeyDownCapture, true);
            targetEl.removeEventListener("pointerdown", onPointerDownCapture, true);
            targetEl.removeEventListener("contextmenu", onContextMenuCapture, true);
          };
        }
        break;
      }
    }

    return () => {
      unregisterDrawAbortListeners?.();
      const m = mapRef.current;
      if (!m) {
        return;
      }
      if (drawInteractionRef.current) {
        m.removeInteraction(drawInteractionRef.current);
        drawInteractionRef.current = null;
      }
      if (modifyInteractionRef.current) {
        m.removeInteraction(modifyInteractionRef.current);
        modifyInteractionRef.current = null;
      }
      if (translateInteractionRef.current) {
        m.removeInteraction(translateInteractionRef.current);
        translateInteractionRef.current = null;
      }
      if (edgeMoveInteractionRef.current) {
        m.removeInteraction(edgeMoveInteractionRef.current);
        edgeMoveInteractionRef.current = null;
      }
      if (rotateShapeInteractionRef.current) {
        m.removeInteraction(rotateShapeInteractionRef.current);
        rotateShapeInteractionRef.current = null;
      }
      if (scaleShapeInteractionRef.current) {
        m.removeInteraction(scaleShapeInteractionRef.current);
        scaleShapeInteractionRef.current = null;
      }
      if (selectInteractionRef.current) {
        m.removeInteraction(selectInteractionRef.current);
        selectInteractionRef.current = null;
      }
      if (dragBoxInteractionRef.current) {
        m.removeInteraction(dragBoxInteractionRef.current);
        dragBoxInteractionRef.current = null;
      }
      if (mvtTileSelectInteractionRef.current) {
        m.removeInteraction(mvtTileSelectInteractionRef.current);
        mvtTileSelectInteractionRef.current = null;
      }
    };
  }, [mapTool, drawShapeMode, setActiveDrawFeature, clearMvtTileSelection]);

  const applyMvtLayerVisibility = useCallback(() => {
    const baseWms = baseRiskWmsLayerRef.current;
    const base = baseRiskLayerRef.current;
    const stored = storedOverrideLayerRef.current;
    const fl = finalComposedLayerRef.current;
    if (!base && !baseWms) {
      return;
    }
    if (mergePreviewOnlyRef.current && fl) {
      baseWms?.setVisible(false);
      base?.setVisible(false);
      stored?.setVisible(false);
      fl.setVisible(true);
      // fl.setOpacity(RISK_MAP_LAYER_OPACITY);
    } else {
      const showBase = showBaseMvtLayerRef.current;
      baseWms?.setVisible(showBase);
      base?.setVisible(showBase);
      stored?.setVisible(showStoredMvtLayerRef.current);
      // baseWms?.setOpacity(RISK_MAP_LAYER_OPACITY);
      // base?.setOpacity(RISK_MAP_LAYER_OPACITY);
      // stored?.setOpacity(RISK_MAP_LAYER_OPACITY);
      if (fl) {
        fl.setVisible(false);
        // fl.setOpacity(RISK_MAP_LAYER_OPACITY);
      }
    }
  }, []);

  /**
   * Couches MVT : référentiel (base) + overrides persistés (PostGIS).
   * Couche « finale » optionnelle : WMS PNG masquée par défaut ; visible seule en mode aperçu (rendu GeoServer).
   * Le tracé en cours reste sur `drawLayer` (au-dessus).
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    setMergePreviewOnly(false);
    mergePreviewOnlyRef.current = false;
    showBaseMvtLayerRef.current = true;
    showStoredMvtLayerRef.current = true;
    setShowBaseMvtLayer(true);
    setShowStoredMvtLayer(true);

    if (baseRiskWmsLayerRef.current) {
      map.removeLayer(baseRiskWmsLayerRef.current);
      baseRiskWmsLayerRef.current = null;
    }
    if (baseRiskLayerRef.current) {
      map.removeLayer(baseRiskLayerRef.current);
      baseRiskLayerRef.current = null;
    }
    if (storedOverrideLayerRef.current) {
      map.removeLayer(storedOverrideLayerRef.current);
      storedOverrideLayerRef.current = null;
    }
    if (finalComposedLayerRef.current) {
      map.removeLayer(finalComposedLayerRef.current);
      finalComposedLayerRef.current = null;
    }
    refreshPublishedTilesRef.current = null;
    refreshFinalTilesOnlyRef.current = null;
    mvtTileSelectedFeaturesRef.current?.clear();

    const geo = getOverrideDatasetGeoLayers(dataset);
    if (!geo) {
      return;
    }

    const overrideViewParams = buildMapTenantViewParams(mapScope, mapTenantId);

    const cacheBust = Date.now();
    const basePreviewMaxZoom =
      geo.basePreviewMaxZoom ?? geo.baseMinZoom ?? undefined;

    const layers = map.getLayers();
    const insertAt = Math.min(1, layers.getLength());
    let layerInsertOffset = 0;

    let baseWmsRefresh: (() => void) | undefined;
    if (basePreviewMaxZoom != null) {
      const baseWmsLayer = createGeoServerRiskTileLayer({
        layers: geo.base,
        opacity: RISK_MAP_LAYER_OPACITY,
        zIndex: 7,
        className: "risk-override-base-wms-layer",
        cacheBustId: cacheBust,
        maxZoom: basePreviewMaxZoom,
      });
      layers.insertAt(insertAt + layerInsertOffset, baseWmsLayer);
      layerInsertOffset += 1;
      baseRiskWmsLayerRef.current = baseWmsLayer;
      const wmsSource = baseWmsLayer.getSource();
      baseWmsRefresh = () => {
        if (!wmsSource) {
          return;
        }
        wmsSource.updateParams({ _v: String(Date.now()) });
        wmsSource.refresh();
      };
    } else {
      baseRiskWmsLayerRef.current = null;
    }

    const baseBundle = createGeoServerMvtFromWmsLayer({
      layers: geo.base,
      opacity: RISK_MAP_LAYER_OPACITY,
      zIndex: 8,
      className: "risk-override-base-mvt-layer",
      style: MVT_RISK_LEVEL_STYLE,
      cacheBustId: cacheBust,
      ...(geo.baseMinZoom != null ? { minZoom: geo.baseMinZoom } : {}),
    });

    layers.insertAt(insertAt + layerInsertOffset, baseBundle.layer);
    layerInsertOffset += 1;
    baseRiskLayerRef.current = baseBundle.layer;

    let storedRefresh: (() => void) | undefined;
    if (geo.overrides) {
      const storedBundle = createGeoServerMvtFromWmsLayer({
        layers: geo.overrides,
        viewParams: overrideViewParams,
        opacity: RISK_MAP_LAYER_OPACITY,
        zIndex: 10,
        className: "risk-override-stored-mvt-layer",
        style: MVT_RISK_LEVEL_STYLE,
        cacheBustId: cacheBust + 1,
      });
      layers.insertAt(insertAt + layerInsertOffset, storedBundle.layer);
      layerInsertOffset += 1;
      storedOverrideLayerRef.current = storedBundle.layer;
      storedRefresh = () => storedBundle.refreshTiles();
    } else {
      storedOverrideLayerRef.current = null;
      showStoredMvtLayerRef.current = false;
      setShowStoredMvtLayer(false);
    }

    let finalRefresh: (() => void) | undefined;
    if (geo.final) {
      const finalWmsLayer = createGeoServerRiskTileLayer({
        layers: geo.final,
        viewParams: overrideViewParams,
        injectTileBboxViewParams: true,
        opacity: RISK_MAP_LAYER_OPACITY,
        zIndex: 11,
        className: "risk-override-final-wms-layer",
        cacheBustId: cacheBust + 2,
      });
      layers.insertAt(insertAt + layerInsertOffset, finalWmsLayer);
      layerInsertOffset += 1;
      finalComposedLayerRef.current = finalWmsLayer;
      finalWmsLayer.setVisible(false);
      const finalWmsSource = finalWmsLayer.getSource();
      finalRefresh = () => {
        if (!finalWmsSource) {
          return;
        }
        finalWmsSource.updateParams({ _v: String(Date.now()) });
        finalWmsSource.refresh();
      };
    }

    refreshFinalTilesOnlyRef.current = finalRefresh ?? null;

    refreshPublishedTilesRef.current = () => {
      baseWmsRefresh?.();
      baseBundle.refreshTiles();
      storedRefresh?.();
      finalRefresh?.();
    };

    applyMvtLayerVisibility();

    return () => {
      refreshPublishedTilesRef.current = null;
      refreshFinalTilesOnlyRef.current = null;
      const m = mapRef.current;
      if (!m) {
        return;
      }
      if (baseRiskWmsLayerRef.current) {
        m.removeLayer(baseRiskWmsLayerRef.current);
        baseRiskWmsLayerRef.current = null;
      }
      if (baseRiskLayerRef.current) {
        m.removeLayer(baseRiskLayerRef.current);
        baseRiskLayerRef.current = null;
      }
      if (storedOverrideLayerRef.current) {
        m.removeLayer(storedOverrideLayerRef.current);
        storedOverrideLayerRef.current = null;
      }
      if (finalComposedLayerRef.current) {
        m.removeLayer(finalComposedLayerRef.current);
        finalComposedLayerRef.current = null;
      }
    };
  }, [dataset, mapScope, mapTenantId, applyMvtLayerVisibility]);

  useEffect(() => {
    applyMvtLayerVisibility();
    if (mergePreviewOnly) {
      refreshFinalTilesOnlyRef.current?.();
    }
  }, [mergePreviewOnly, showBaseMvtLayer, showStoredMvtLayer, applyMvtLayerVisibility]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      map.updateSize();
    });
    return () => window.cancelAnimationFrame(id);
  }, [mergePreviewOnly]);

  const clearShape = () => {
    drawSourceRef.current?.clear();
    selectedFeaturesRef.current?.clear();
    setActiveDrawFeature(null);
    clearMvtTileSelection();
    setFeatureCount(0);
    setSuccessMessage(null);
  };

  const deleteSelectedShapes = () => {
    const source = drawSourceRef.current;
    const selected = selectedFeaturesRef.current;
    if (!source || !selected || selected.getLength() === 0) {
      return;
    }
    const toRemove = selected.getArray().slice();
    toRemove.forEach((f) => source.removeFeature(f));
    selected.clear();
    const remaining = source.getFeatures();
    setActiveDrawFeature(
      remaining.length > 0 ? (remaining[remaining.length - 1] as Feature) : null,
    );
    clearMvtTileSelection();
    setFeatureCount(remaining.length);
    setSuccessMessage(null);
  };

  /** API PostGIS attend du GeoJSON surfacique ; un cercle OL résiduel est converti en octogone. */
  function geometryForApiExport(raw: import("ol/geom/Geometry").default) {
    if (raw.getType() === "Circle") {
      return fromCircle(raw as CircleGeom, CIRCLE_TO_POLYGON_SIDES);
    }
    return raw;
  }

  void mvtSelectionTick;
  const mvtInspectorList = mvtTileSelectedFeaturesRef.current?.getArray() ?? [];

  const copyMvtSelectionToDrawLayer = async () => {
    const col = mvtTileSelectedFeaturesRef.current;
    const drawSrc = drawSourceRef.current;
    const selected = selectedFeaturesRef.current;
    if (!col || !drawSrc || col.getLength() === 0) {
      return;
    }
    let lastAdded: Feature | null = null;
    for (const raw of col.getArray()) {
      const feat = raw as Feature;
      const geom = feat.getGeometry();
      if (!geom) {
        continue;
      }
      let params = readMvtOverrideParams(feat);
      const idVal = resolveMvtFeatureIdForWfs(feat);
      if (isMvtFeatureFromPersistedOverrides(feat, dataset) && idVal != null) {
        const rowId = typeof idVal === "number" ? idVal : Number(idVal);
        const mvtHasKind =
          mvtFeatureProp(feat, ["override_kind", "overrideKind"]) !== undefined;
        const mvtHasStatus = mvtFeatureProp(feat, ["status"]) !== undefined;
        if (
          Number.isFinite(rowId) &&
          rowId > 0 &&
          (!mvtHasKind || !mvtHasStatus)
        ) {
          try {
            const res = await getRiskOverride(Math.trunc(rowId), dataset);
            if (res.override) {
              params = paramsFromRiskOverrideDto(res.override);
            }
          } catch {
            /* attributs tuile MVT ou table absente : on garde readMvtOverrideParams */
          }
        }
      }
      const nf = new Feature({ geometry: geom.clone() });
      writeDrawFeatureParams(nf, params);
      if (idVal != null) {
        if (isMvtFeatureFromPersistedOverrides(feat, dataset)) {
          nf.set(OVERRIDE_EXISTING_OVERRIDE_ID, Number(idVal));
        } else {
          nf.set(OVERRIDE_SOURCE_FEATURE_ID, idVal);
        }
      }
      drawSrc.addFeature(nf);
      lastAdded = nf;
    }
    clearMvtTileSelection();
    if (lastAdded && selected) {
      selected.clear();
      selected.push(lastAdded);
      setActiveDrawFeature(lastAdded);
      setMapTool("select");
    }
    setFeatureCount(drawSrc.getFeatures().length);
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  const loadWfsFullGeometryForFeature = async (feat: Feature) => {
    const idVal = resolveMvtFeatureIdForWfs(feat);
    const typeName = resolveOverrideDatasetWfsTypeName(dataset, feat);
    if (idVal == null || !typeName) {
      setWfsLastError(
        "Impossible de résoudre l’identifiant ou le nom de couche WFS pour cette entité.",
      );
      return;
    }
    if (mapScope === "tenant" && mapTenantId.trim() === "") {
      setWfsLastError(
        "Renseignez le tenant (contexte carte) pour interroger les couches filtrées.",
      );
      return;
    }
    const wfsViewParams = buildMapTenantViewParams(mapScope, mapTenantId);
    const isOverrideLayer = typeName.includes("override");
    const uid = String(getUid(feat));
    setWfsLoadingUid(uid);
    setWfsLastError(null);
    try {
      const gj = await fetchGeoServerFeatureJsonById({
        typeName,
        idValue: idVal,
        viewParams: isOverrideLayer ? wfsViewParams : undefined,
      });
      if (!gj?.geometry) {
        throw new Error(
          "Aucune géométrie dans la réponse WFS (id inconnu ou filtres VIEWPARAMS).",
        );
      }
      const format = new GeoJSON();
      const geom = format.readGeometry(
        gj.geometry as import("geojson").Geometry,
        {
          dataProjection: "EPSG:3857",
          featureProjection: "EPSG:3857",
        },
      );
      feat.setGeometry(geom);
      feat.set("_wfsFullGeometryLoaded", true);
      feat.changed();
      setMvtSelectionTick((n) => n + 1);
    } catch (err: unknown) {
      let msg = "Échec WFS.";
      if (typeof err === "object" && err !== null && "response" in err) {
        const r = (err as { response?: { data?: unknown } }).response;
        if (r?.data != null) {
          msg =
            typeof r.data === "string"
              ? r.data
              : JSON.stringify(r.data).slice(0, 500);
        }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setWfsLastError(msg);
    } finally {
      setWfsLoadingUid(null);
    }
  };

  function persistedOverrideRowId(feat: Feature): number | null {
    if (!isMvtFeatureFromPersistedOverrides(feat, dataset)) {
      return null;
    }
    const raw = resolveMvtFeatureIdForWfs(feat);
    if (raw == null) {
      return null;
    }
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
  }

  const deletePersistedOverrideFromInspector = async (feat: Feature) => {
    const rowId = persistedOverrideRowId(feat);
    if (rowId == null) {
      setWfsLastError("Suppression : id d’override introuvable ou non numérique.");
      return;
    }
    if (!window.confirm(`Supprimer l’override en base (id=${rowId}) ?`)) {
      return;
    }
    const uid = String(getUid(feat));
    setMvtDeletingUid(uid);
    setWfsLastError(null);
    try {
      await deleteRiskOverride(rowId, dataset);
      const col = mvtTileSelectedFeaturesRef.current;
      const hs = mvtHighlightSourceRef.current;
      const removedFromSelection: Feature[] = [];
      const clickedUid = String(getUid(feat));
      if (col) {
        for (const f of col.getArray().slice()) {
          if (
            persistedOverrideRowId(f) === rowId ||
            String(getUid(f)) === clickedUid
          ) {
            removedFromSelection.push(f);
            col.remove(f);
          }
        }
        if (removedFromSelection.length === 0 && col.remove(feat) !== undefined) {
          removedFromSelection.push(feat);
        }
      }
      if (hs && removedFromSelection.length === 0) {
        for (const hf of hs.getFeatures().slice()) {
          if (persistedOverrideRowId(hf as Feature) === rowId) {
            hs.removeFeature(hf as Feature);
          }
        }
      }
      const mvtSel = mvtTileSelectInteractionRef.current as unknown as {
        featureLayerAssociation_: Record<string, unknown> | undefined;
      } | null;
      if (mvtSel?.featureLayerAssociation_) {
        const assoc = mvtSel.featureLayerAssociation_;
        for (const f of removedFromSelection) {
          delete assoc[String(getUid(f))];
        }
        delete assoc[clickedUid];
      }
      mvtHighlightLayerRef.current?.changed();
      refreshPublishedTilesRef.current?.();
      setMvtSelectionTick((n) => n + 1);
    } catch (err: unknown) {
      let msg = "Échec de la suppression.";
      if (typeof err === "object" && err !== null && "response" in err) {
        const r = (err as { response?: { data?: { message?: string } } }).response;
        if (r?.data?.message != null) {
          msg = String(r.data.message);
        }
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setWfsLastError(msg);
    } finally {
      setMvtDeletingUid(null);
    }
  };

  const saveOverride = async () => {
    const source = drawSourceRef.current;
    const features = source?.getFeatures() ?? [];
    if (features.length === 0) {
      setErrorMessage("Tracez au moins une forme sur la carte avant de sauvegarder.");
      setSuccessMessage(null);
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      let lastCreatedId: string | number | undefined;
      let created = 0;
      for (const feature of features) {
        const rawGeometry = feature.getGeometry();
        if (!rawGeometry) {
          setErrorMessage("Une des géométries est invalide.");
          setSaving(false);
          return;
        }
        const geometry = geometryForApiExport(rawGeometry);
        const geometryGeoJson = new GeoJSON().writeGeometryObject(geometry, {
          featureProjection: "EPSG:3857",
          dataProjection: "EPSG:4326",
        });
        const fp = readDrawFeatureParams(feature);
        const featureRiskLevel = fp.riskLevel;
        const featureKind = fp.overrideKind;
        const featureScope = fp.scope;
        const featureTenant =
          featureScope === "tenant" && fp.tenantId.trim() !== ""
            ? fp.tenantId.trim()
            : null;

        if (featureScope === "tenant" && !featureTenant) {
          setErrorMessage("tenantId est requis pour un override tenant.");
          setSaving(false);
          return;
        }

        const rpParsed = parseReturnPeriodInput(fp.returnPeriod);
        if (!rpParsed.ok) {
          setErrorMessage(rpParsed.error);
          setSaving(false);
          return;
        }

        const existingOid = feature.get(OVERRIDE_EXISTING_OVERRIDE_ID);
        const sourceFidRaw = feature.get(OVERRIDE_SOURCE_FEATURE_ID);
        const sourceFidStr =
          sourceFidRaw != null && String(sourceFidRaw).trim() !== ""
            ? String(sourceFidRaw).trim()
            : null;

        if (typeof existingOid === "number" && Number.isFinite(existingOid)) {
          await patchRiskOverride(existingOid, {
            dataset,
            geometry: geometryGeoJson as unknown as Record<string, unknown>,
            riskLevel:
              featureKind === "exclude" ? null : featureRiskLevel,
            status: fp.status,
            priority: fp.priority,
            note: fp.note.trim() || null,
            overrideKind: featureKind,
            sourceFeatureId: sourceFidStr,
            returnPeriodMin: rpParsed.min,
            returnPeriodMax: rpParsed.max,
          });
          created += 1;
          lastCreatedId = existingOid;
          refreshPublishedTilesRef.current?.();
          continue;
        }

        const response = await createRiskOverride({
          dataset,
          scope: featureScope,
          tenantId: featureTenant,
          geometry: geometryGeoJson as unknown as Record<string, unknown>,
          riskLevel: featureKind === "exclude" ? null : featureRiskLevel,
          status: fp.status,
          priority: fp.priority,
          note: fp.note.trim(),
          overrideKind: featureKind,
          sourceFeatureId: sourceFidStr,
          upsert:
            upsertBySource && sourceFidStr != null,
          returnPeriodMin: rpParsed.min,
          returnPeriodMax: rpParsed.max,
        });
        created += 1;
        if (response?.override?.id != null) {
          lastCreatedId = response.override.id;
        }
        refreshPublishedTilesRef.current?.();
      }

      setSuccessMessage(
        created > 1
          ? lastCreatedId != null
            ? `${created} opérations enregistrées (dernier id=${lastCreatedId}).`
            : `${created} opérations enregistrées.`
          : lastCreatedId != null
            ? `Enregistré (id=${lastCreatedId}).`
            : "Enregistrement réussi."
      );
      drawSourceRef.current?.clear();
      selectedFeaturesRef.current?.clear();
      setActiveDrawFeature(null);
      setFeatureCount(0);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        error?.message ??
        "Erreur lors de la sauvegarde de l'override.";
      setErrorMessage(String(message));
    } finally {
      setSaving(false);
    }
  };

  const geoForMap = getOverrideDatasetGeoLayers(dataset);
  const hasMvtLayers = Boolean(geoForMap);
  const hasStoredOverrideLayer = Boolean(geoForMap?.overrides);
  const hasFinalPreviewLayer = Boolean(geoForMap?.final);

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 112px)",
        maxHeight: "calc(100vh - 112px)",
        minHeight: 480,
        display: "flex",
        gap: 2,
        alignItems: "stretch",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: 360,
          flexShrink: 0,
          minHeight: 0,
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6">Risk overrides</Typography>
          <Typography variant="body2" color="text.secondary">
            Palette verticale : type de forme, puis outils (dessin, déplacer, pivoter, taille, côté, sommet, sélection tuiles MVT, sélection des tracés).
            Carte : référentiel et overrides persistés en MVT (couleurs client `scaleLevels`). Le mode aperçu « carte finale » affiche la couche fusion PostGIS en WMS PNG (style GeoServer), via le bouton « Mode aperçu » lorsque la couche est publiée.
            Vos tracés (couleur niveau de risque) sont au-dessus jusqu’à sauvegarde. Après « Save », les tuiles se rechargent.
            Vous pouvez tracer plusieurs polygones ; chaque forme est enregistrée comme un override distinct à la
            sauvegarde. Maj+clic en mode sélection pour ajouter ou retirer une forme ; Ctrl/⌘+glisser trace un cadre de sélection.
            Avec au moins une forme sélectionnée : Ctrl (Windows) ou ⌘ (Mac) + molette sur la carte pour ajuster le niveau de risque (0–5).
            Survolez une icône pour le détail.
          </Typography>

          <Typography variant="caption" color="text.secondary" display="block">
            {mapTool === "draw" &&
              "Mode dessin : cliquez pour tracer. Échap (hors champ de saisie) ou clic droit sur la carte : annuler le tracé en cours."}
            {mapTool === "moveShape" && "Déplacer la forme : glissez la surface ou le contour."}
            {mapTool === "rotateShape" &&
              "Pivoter : cliquez sur la forme, puis tournez en glissant (rotation autour du centre)."}
            {mapTool === "scaleShape" &&
              "Taille : cliquez sur la forme, puis éloignez ou rapprochez le curseur du centre pour agrandir ou réduire."}
            {mapTool === "moveEdge" &&
              "Côté : approchez le milieu d’un côté (snap comme en mode sommet), puis glissez pour déplacer les deux extrémités ensemble. Évitez les coins (zone sommet)."}
            {mapTool === "addVertex" &&
              "Sommet : cliquez sur un côté pour insérer un sommet, ou déplacez les sommets existants."}
            {mapTool === "select" &&
              "Sélection : clic sur une forme ; Maj+clic pour ajouter ou retirer. Cadre : Ctrl (Windows) ou ⌘ (Mac) + glisser pour tout ce qui intersecte le rectangle ; maintenez Maj au relâchement pour ajouter au lieu de remplacer. Ctrl ou ⌘ + molette : niveau de risque des formes sélectionnées."}
            {mapTool === "selectMvt" &&
              `Sélection tuiles : un clic sélectionne tout ce qui est sous le pixel (plusieurs calques / chevauchements). Maj+clic : ajoute ou retire les entités au point. Panneau de droite : attributs MVT. ${mvtTileSelectedCount} entité(s). Filtre MVT override/finale = périmètre carte (VIEWPARAM tenant_id). La sélection est effacée si vous changez de dataset / périmètre ou si les tuiles sont reconstruites.`}
          </Typography>

          {hasMvtLayers && (
            <Stack spacing={0.25}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Calques MVT (hors mode aperçu)
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showBaseMvtLayer}
                    disabled={mergePreviewOnly}
                    onChange={(_, c) => setShowBaseMvtLayer(c)}
                    size="small"
                  />
                }
                label="Référentiel (dataset source)"
              />
              {hasStoredOverrideLayer && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showStoredMvtLayer}
                      disabled={mergePreviewOnly}
                      onChange={(_, c) => setShowStoredMvtLayer(c)}
                      size="small"
                    />
                  }
                  label="Overrides persistés"
                />
              )}
            </Stack>
          )}

          {hasFinalPreviewLayer && (
            <Stack spacing={1}>
              <Button
                variant={mergePreviewOnly ? "contained" : "outlined"}
                color={mergePreviewOnly ? "success" : "primary"}
                fullWidth
                onClick={() => setMergePreviewOnly((v) => !v)}
              >
                {mergePreviewOnly
                  ? "Quitter le mode aperçu"
                  : "Mode aperçu (carte finale seule)"}
              </Button>
              {mergePreviewOnly && (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<Refresh />}
                  onClick={() => refreshFinalTilesOnlyRef.current?.()}
                >
                  Rafraîchir l’aperçu
                </Button>
              )}
            </Stack>
          )}
          <Typography variant="subtitle2" color="text.secondary">
            Contexte carte
          </Typography>
          <TextField
            select
            label="Dataset"
            value={dataset}
            onChange={(event) => setDataset(event.target.value)}
            size="small"
          >
            {DATASET_OPTIONS.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Périmètre carte"
            value={mapScope}
            onChange={(event) =>
              setMapScope(event.target.value as RiskOverrideScope)
            }
            size="small"
          >
            <MenuItem value="global">Global (overrides globaux)</MenuItem>
            <MenuItem value="tenant">Tenant (global + ce tenant)</MenuItem>
          </TextField>

          {mapScope === "tenant" && (
            <TextField
              label="Tenant ID (contexte carte)"
              value={mapTenantId}
              onChange={(event) => setMapTenantId(event.target.value)}
              size="small"
              required
              helperText="VIEWPARAM GeoServer : overrides globaux + overrides de ce tenant."
            />
          )}

          {mapScope === "global" && (
            <Typography variant="caption" color="text.secondary" display="block">
              Couches override et carte finale : uniquement les overrides scope global.
            </Typography>
          )}

          <Divider sx={{ my: 0.5 }} />

          <Typography variant="subtitle2" color="text.secondary">
            Propriétés de la forme
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {activeDrawFeatureUid
              ? `Forme active (${selectedCount} sélectionnée(s) au total) : contour bleu/blanc. Les champs ci-dessous modifient cette forme.`
              : "Aucune forme active : les valeurs servent de défaut pour le prochain tracé ou la copie MVT."}
          </Typography>

          <TextField
            select
            label="Type d'override"
            value={overrideKind}
            onChange={(event) =>
              patchActiveShapeForm({
                overrideKind: event.target.value as RiskOverrideKind,
              })
            }
            size="small"
          >
            <MenuItem value="replace">
              Remplacer le risque (même zone ou nouvelle géométrie)
            </MenuItem>
            <MenuItem value="add">Ajouter une zone</MenuItem>
            <MenuItem value="exclude">Exclure du rendu final (masque)</MenuItem>
          </TextField>

          <TextField
            select
            label="Scope"
            value={scope}
            onChange={(event) =>
              patchActiveShapeForm({
                scope: event.target.value as RiskOverrideScope,
              })
            }
            size="small"
          >
            <MenuItem value="global">Global</MenuItem>
            <MenuItem value="tenant">Tenant</MenuItem>
          </TextField>

          {scope === "tenant" && (
            <TextField
              label="Tenant ID"
              value={tenantId}
              onChange={(event) =>
                patchActiveShapeForm({ tenantId: event.target.value })
              }
              size="small"
              required
            />
          )}

          <TextField
            label="Risk level"
            type="number"
            inputProps={{ min: 0, max: 5, step: 1 }}
            value={riskLevel}
            onChange={(event) =>
              patchActiveShapeForm({ riskLevel: Number(event.target.value) })
            }
            size="small"
            disabled={overrideKind === "exclude"}
          />

          {overrideKind === "exclude" && (
            <Typography variant="caption" color="text.secondary">
              Masque : le niveau de risque n’est pas utilisé en base (NULL).
            </Typography>
          )}

          <TextField
            select
            label="Status"
            value={status}
            onChange={(event) =>
              patchActiveShapeForm({
                status: event.target.value as RiskOverrideStatus,
              })
            }
            size="small"
          >
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
          </TextField>

          <TextField
            label="Priority"
            type="number"
            value={priority}
            onChange={(event) =>
              patchActiveShapeForm({ priority: Number(event.target.value) })
            }
            size="small"
          />

          <TextField
            label="Période de retour (ans)"
            value={returnPeriod}
            onChange={(event) =>
              patchActiveShapeForm({ returnPeriod: event.target.value })
            }
            size="small"
            placeholder="ex. 10, 10-30, 1000+"
            helperText="Vide = aucune · 10 = exact · 10-30 = intervalle · 1000+ = au moins 1000 ans"
          />

          <TextField
            label="Note"
            value={note}
            onChange={(event) => patchActiveShapeForm({ note: event.target.value })}
            size="small"
            multiline
            minRows={2}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={upsertBySource}
                onChange={(_, checked) => setUpsertBySource(checked)}
                size="small"
              />
            }
            label="Upsert si id référentiel (évite les doublons pour la même entité source)"
          />

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={saveOverride} disabled={saving || featureCount === 0}>
              {saving ? "Saving..." : "Save override"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={deleteSelectedShapes}
              disabled={selectedCount === 0 || saving}
            >
              Effacer la sélection ({selectedCount})
            </Button>
            <Button variant="outlined" onClick={clearShape} disabled={featureCount === 0 || saving}>
              Effacer toutes les formes
            </Button>
          </Stack>

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          border: "1px solid rgba(0,0,0,0.2)",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            position: "relative",
            height: "100%",
          }}
        >
        <Paper
          elevation={4}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            left: "auto",
            zIndex: 10,
            py: 0.75,
            px: 0.5,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.25,
            maxHeight: "calc(100% - 24px)",
            overflowY: "auto",
            pointerEvents: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <ToggleButtonGroup
            orientation="vertical"
            exclusive
            size="small"
            value={drawShapeMode}
            onChange={(_, value: DrawShapeMode | null) => {
              if (value != null) {
                setDrawShapeMode(value);
                setMapTool("draw");
              }
            }}
            aria-label="Type de forme"
            sx={{
              "& .MuiToggleButton-root": {
                minWidth: 40,
                px: 0,
                py: 0.75,
                borderRadius: "8px !important",
              },
            }}
          >
            <ToggleButton
              value="polygon"
              aria-label="Polygone libre"
              title="Polygone (tracé libre)"
            >
              <Polyline fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="octagon"
              aria-label="Octogone régulier"
              title="Octogone régulier (8 côtés)"
            >
              <HexagonOutlined fontSize="small" />
            </ToggleButton>
            <ToggleButton value="square" aria-label="Carré" title="Carré régulier">
              <CropSquare fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider flexItem sx={{ my: 0.5, borderColor: "divider" }} />

          <ToggleButtonGroup
            orientation="vertical"
            exclusive
            size="small"
            value={mapTool}
            onChange={(_, value: MapEditTool | null) => {
              if (value != null) {
                setMapTool(value);
              }
            }}
            aria-label="Outil de transformation"
            sx={{
              "& .MuiToggleButton-root": {
                minWidth: 40,
                px: 0,
                py: 0.75,
                borderRadius: "8px !important",
              },
            }}
          >
            <ToggleButton value="draw" aria-label="Dessiner" title="Dessiner (selon la forme choisie)">
              <Create fontSize="small" />
            </ToggleButton>
            <ToggleButton value="moveShape" aria-label="Déplacer la forme" title="Déplacer toute la forme">
              <OpenWith fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="rotateShape"
              aria-label="Pivoter la forme"
              title="Pivoter autour du centre de la forme"
            >
              <RotateRight fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="scaleShape"
              aria-label="Mettre à l’échelle"
              title="Redimensionner depuis le centre (glisser pour zoomer la forme)"
            >
              <PhotoSizeSelectLarge fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="moveEdge"
              aria-label="Déplacer un côté"
              title="Côté entier (2 sommets) : snap sur l’arête, puis glisser. Éviter les coins."
            >
              <LineStyle fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="addVertex"
              aria-label="Ajouter un sommet"
              title="Cliquer sur un côté pour insérer un sommet"
            >
              <AddLocationAlt fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="selectMvt"
              aria-label="Sélectionner les polygones MVT"
              title="Tuiles vectorielles : référentiel + overrides persistés. Clic / Maj+clic."
            >
              <LayersOutlined fontSize="small" />
            </ToggleButton>
            <ToggleButton
              value="select"
              aria-label="Sélectionner des formes"
              title="Clic / Maj+clic ; Ctrl ou ⌘ + glisser : cadre de sélection"
            >
              <TouchApp fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Paper>
        <Box ref={mapElementRef} sx={{ width: "100%", height: "100%", minHeight: 360 }} />
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            bottom: 12,
            left: 12,
            zIndex: 10,
            px: 1.25,
            py: 0.5,
            pointerEvents: "none",
            bgcolor: "rgba(255,255,255,0.92)",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="caption" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
            Zoom{" "}
            {mapZoomDisplay != null ? mapZoomDisplay.toFixed(2) : "—"}
            {geoForMap?.baseMinZoom != null
              ? ` · réf. WMS < ${geoForMap.basePreviewMaxZoom ?? geoForMap.baseMinZoom} · MVT ≥ ${geoForMap.baseMinZoom}`
              : ""}
          </Typography>
        </Paper>
        </Box>
        {mapTool === "selectMvt" && (
          <Paper
            elevation={0}
            square
            sx={{
              width: 300,
              flexShrink: 0,
              minHeight: 0,
              height: "100%",
              alignSelf: "stretch",
              borderLeft: "1px solid",
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="subtitle2" sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: "divider" }}>
              Entités MVT sélectionnées ({mvtInspectorList.length})
            </Typography>
            {mvtInspectorList.length > 0 && (
              <Box sx={{ px: 1, pb: 0.5 }}>
                <Button
                  size="small"
                  fullWidth
                  variant="contained"
                  color="secondary"
                  onClick={() => void copyMvtSelectionToDrawLayer()}
                >
                  Copier vers le calque d’édition
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Référentiel → lien upsert ; couche overrides persistés → mise à jour (PATCH) ou suppression en base.
                </Typography>
              </Box>
            )}
            {wfsLastError && (
              <Alert
                severity="error"
                onClose={() => setWfsLastError(null)}
                sx={{ mx: 1, mt: 0.5, py: 0 }}
              >
                {wfsLastError}
              </Alert>
            )}
            <Box sx={{ overflow: "auto", flex: 1, minHeight: 0, px: 0.5, pb: 1 }}>
              {mvtInspectorList.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1.5 }}>
                  Cliquez sur la carte (outil calques). Les attributs viennent des tuiles GeoServer.
                </Typography>
              ) : (
                mvtInspectorList.map((feat, index) => {
                  const layerLabel = String(feat.get("_mvtLayerLabel") ?? "—");
                  const fid = feat.getId();
                  const title =
                    fid !== undefined
                      ? `#${index + 1} · ${layerLabel} · id=${String(fid)}`
                      : `#${index + 1} · ${layerLabel}`;
                  const rows = mvtInspectorPropertyRows(feat);
                  const featUid = String(getUid(feat));
                  const canWfs =
                    resolveMvtFeatureIdForWfs(feat) != null &&
                    resolveOverrideDatasetWfsTypeName(dataset, feat) != null &&
                    (mapScope !== "tenant" || mapTenantId.trim() !== "");
                  const canDeletePersistedOverride = persistedOverrideRowId(feat) != null;
                  return (
                    <Accordion key={`${getUid(feat)}-${index}`} disableGutters defaultExpanded={false}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ px: 1, minHeight: 44 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                          {title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0, px: 0.5, pb: 1 }}>
                        <Stack spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              !canWfs || wfsLoadingUid === featUid
                            }
                            onClick={() => void loadWfsFullGeometryForFeature(feat)}
                          >
                            {wfsLoadingUid === featUid
                              ? "Chargement WFS…"
                              : "Géométrie complète (WFS)"}
                          </Button>
                          {feat.get("_wfsFullGeometryLoaded") && (
                            <Typography variant="caption" color="success.main">
                              Géométrie serveur appliquée sur la surbrillance (remplace le fragment tuile).
                            </Typography>
                          )}
                          {!canWfs && (
                            <Typography variant="caption" color="text.secondary">
                              WFS : identifiant ou couche indisponible, ou tenant manquant.
                            </Typography>
                          )}
                          {canDeletePersistedOverride && (
                            <Button
                              size="small"
                              fullWidth
                              variant="outlined"
                              color="error"
                              disabled={mvtDeletingUid === featUid}
                              onClick={() => void deletePersistedOverrideFromInspector(feat)}
                            >
                              {mvtDeletingUid === featUid
                                ? "Suppression…"
                                : "Supprimer l’override (base)"}
                            </Button>
                          )}
                          {rows.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">
                              Aucun attribut hors géométrie (tuile).
                            </Typography>
                          ) : (
                            <Table size="small" padding="none">
                              <TableBody>
                                {rows.map((row) => (
                                  <TableRow key={row.key}>
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      sx={{
                                        verticalAlign: "top",
                                        fontWeight: 600,
                                        width: "38%",
                                        pr: 0.5,
                                        border: 0,
                                      }}
                                    >
                                      {row.key}
                                    </TableCell>
                                    <TableCell sx={{ verticalAlign: "top", border: 0, wordBreak: "break-word" }}>
                                      {row.value}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })
              )}
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
